import React, { useCallback, useEffect, useRef } from 'react';
import { PlannerRefs } from './usePlannerRefs';
import { PlannerState } from './usePlannerState';

export const useTimelineWindow = (state: PlannerState, refs: PlannerRefs) => {
    const reachedEdgeRef = useRef<'top' | 'bottom' | null>(null);
    const {
        draftInteraction, draggingTask, isDragging, resizingTask, setActiveTimelineDateKey, setActiveTimelineLabel,
        setTimelineWindow, showSchedule, timelineScale,
    } = state;
    const {
        isProgrammaticTimelineNavigationRef, pendingPrependAnchorRef, pendingPrependHeightRef,
        pendingTimelineCurrentScrollRef, pendingTimelineTaskScrollRef, suppressNextTimelineCurrentScrollRef,
        timelineRef, timelineScrollLockRef,
    } = refs;
    useEffect(() => {
        if (showSchedule) return;
        if (pendingTimelineTaskScrollRef.current || suppressNextTimelineCurrentScrollRef.current) {
            suppressNextTimelineCurrentScrollRef.current = false;
            return;
        }
        pendingTimelineCurrentScrollRef.current = true;
        pendingPrependHeightRef.current = null;
        pendingPrependAnchorRef.current = null;
        timelineScrollLockRef.current = false;
        setActiveTimelineLabel('');
        setActiveTimelineDateKey('');
        setTimelineWindow(timelineScale === 'month' ? { start: 0, end: 0 } : { start: -1, end: 1 });
    }, [pendingPrependAnchorRef, pendingPrependHeightRef, pendingTimelineCurrentScrollRef,
        pendingTimelineTaskScrollRef, setActiveTimelineDateKey, setActiveTimelineLabel, setTimelineWindow,
        showSchedule, suppressNextTimelineCurrentScrollRef, timelineScale, timelineScrollLockRef]);
    const updateActiveTimelineLabel = useCallback(() => {
        const container = timelineRef.current;
        if (!container) return;
        const periods = Array.from(container.querySelectorAll('.timeline-period')) as HTMLElement[];
        if (!periods.length) return;
        const controls = container.querySelector('.timeline-zoom-controls-main') as HTMLElement | null;
        const line = container.getBoundingClientRect().top + (controls?.offsetHeight || 0) + 1;
        let label = periods[0].dataset.timelineLabel || '';
        let dateKey = periods[0].dataset.timelineDate || '';
        periods.forEach((period) => {
            if (period.getBoundingClientRect().top <= line) {
                label = period.dataset.timelineLabel || label;
                dateKey = period.dataset.timelineDate || dateKey;
            }
        });
        setActiveTimelineLabel((current) => current === label ? current : label);
        setActiveTimelineDateKey((current) => current === dateKey ? current : dateKey);
    }, [setActiveTimelineDateKey, setActiveTimelineLabel, timelineRef]);
    const expandTimelineWindowAtEdge = useCallback((container: HTMLDivElement) => {
        if (timelineScrollLockRef.current) return;
        const threshold = 320;
        const max = container.scrollHeight - container.clientHeight;
        const top = max > threshold * 2 && container.scrollTop <= threshold;
        const bottom = container.scrollTop >= max - threshold;
        if (!top && !bottom) {
            reachedEdgeRef.current = null;
            return;
        }
        const edge = top ? 'top' : 'bottom';
        if (reachedEdgeRef.current === edge) return;
        reachedEdgeRef.current = edge;
        timelineScrollLockRef.current = true;
        pendingPrependHeightRef.current = top ? container.scrollHeight : null;
        pendingPrependAnchorRef.current = null;
        const rect = container.getBoundingClientRect();
        const period = (Array.from(container.querySelectorAll('.timeline-period')) as HTMLElement[])
            .find((entry) => entry.getBoundingClientRect().bottom > rect.top);
        if (period?.dataset.timelinePeriodKey) {
            pendingPrependAnchorRef.current = {
                key: period.dataset.timelinePeriodKey,
                top: period.getBoundingClientRect().top - rect.top,
            };
        }
        setTimelineWindow((range) => {
            const recycle = range.end - range.start >= 2;
            if (top) return {
                start: range.start - 1,
                end: recycle ? range.end - 1 : range.end,
            };
            return {
                start: recycle ? range.start + 1 : range.start,
                end: range.end + 1,
            };
        });
    }, [pendingPrependAnchorRef, pendingPrependHeightRef, setTimelineWindow, timelineScrollLockRef]);
    const handleTimelineScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (timelineScale === 'month') return;
        if (showSchedule || isProgrammaticTimelineNavigationRef.current || isDragging
            || draggingTask || resizingTask || draftInteraction) return;
        updateActiveTimelineLabel();
        expandTimelineWindowAtEdge(event.currentTarget);
    };
    return { expandTimelineWindowAtEdge, handleTimelineScroll, updateActiveTimelineLabel };
};
